import {UI} from "../../../../../../stem-core/src/ui/UIBase";

import {BasePanelPage} from "../../../../PanelPage";
import {Messages} from "../../../../../Messages";
import {Flow} from "../../../../flows/FlowState.js";
import {iFrameUserDataService} from "../../../../../services/IFrameUserDataService";
import {LinkButton} from "../../../../../../core/ui/LinkButton.jsx";
import {
    ADDRESS_PANEL_URL,
    INLINE_EDIT_SUBSCRIPTION_URLS,
    PAYMENT_DETAILS_PANEL_URL,
    SUBSCRIPTION_PLAN_PANEL_URL
} from "../../../../PanelConstants";
import {PaymentMethodDescription} from "../../../../../common/PaymentMethodDescription.jsx";
import {SubscriptionApplyPolicy} from "../../../../../../client/state/SubscriptionStore";
import {CheckboxInput} from "../../../../../../core/ui/input/checkbox/CheckboxInput.jsx";
import {MerchantButton} from "../../../../../common/MerchantButton";
import {Router} from "../../../../../../stem-core/src/ui/Router";
import {styleRule, styleRuleInherit} from "../../../../../../stem-core/src/decorators/Style";
import {registerStyle} from "../../../../../../stem-core/src/ui/style/Theme";
import {SubscribeReviewOrderPanelStyle} from "../SubscribeReviewOrderPanel";
import {MessageElement} from "../../../../../widget/ui/MessageElement";
import {UsingBlinkDefaultPaymentMethod} from "../../../../../common/UsingBlinkDefaultPaymentMethod";
import {autoredraw} from "../../../../../../stem-core/src/decorators/AutoRedraw";
import {PaymentMethodStore} from "../../../../../State";
import {iFrameMerchantService} from "../../../../../services/IframeMerchantService";
import {getPaymentMethodEmptySubscriptionMessage} from "../../../../../common/MessagingUtils.js";
import {Controller} from "../../../../flows/Controller.js";


class EditSubscriptionPanelStyle extends SubscribeReviewOrderPanelStyle {
    @styleRuleInherit
    reviewItem = {
        ":first-child": {
            marginTop: 0,
        }
    };

    @styleRuleInherit
    reviewItemTitleContainer = {
        marginBottom: 8,
    };

    @styleRule
    checkbox = {
        color: "inherit",
        fontSize: "inherit",
        marginBottom: 8,
    };

    @styleRule
    cardIcon = {
        display: "inline-block",
        marginRight: 12,
    };

    @styleRule
    cardDetails = {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
    };
}

@autoredraw(PaymentMethodStore)
@registerStyle(EditSubscriptionPanelStyle)
export class EditSubscriptionPanel extends BasePanelPage {
    getDefaultOptions() {
        return {
            ...super.getDefaultOptions(),
            title: Messages.manageSubscription,
        };
    }

    isPrimaryButtonDisabled() {
        const subscription = iFrameUserDataService.getActiveSubscription();
        return Controller.subscriptionPlan.userHasEquivalentSubscriptionToSelected() &&
            subscription.getShippingAddress() === Controller.address.selectedAddress &&
            subscription.getPaymentMethod() === Controller.paymentMethod.selectedPaymentMethod &&
            (subscription.getDiscount()?.getCode() || null) === Controller.subscriptionPlan.discountCode;
    }

    renderCheckboxAndPriceInfo() {
        const {selectedOffer, applyPolicy} = Controller.subscriptionPlan;
        const instantPayInfo = Controller.subscriptionPlan.getDiscountInfoOnInstantChangePolicy(selectedOffer);
        const nextCyclePayInfo = Controller.subscriptionPlan.getDiscountInfo(selectedOffer);
        const activeSubscription = iFrameUserDataService.getActiveSubscription();
        const selectedBillingPlan = selectedOffer == null ? activeSubscription.billingPlan : selectedOffer.billingPlan;

        if (!instantPayInfo) {
            return null;
        }

        if (Controller.subscriptionPlan.userHasEquivalentSubscriptionToSelected() && selectedBillingPlan.formatPrice(nextCyclePayInfo.finalPrice).toString() === activeSubscription.getPrice().toString()) {
            return null;
        }

        const {styleSheet} = this;
        const paidUntil = activeSubscription.getPaidUntil();
        const nextBillingDate = activeSubscription.getNextBillingDate();

        const paymentInfoMessageArgs = [
            selectedBillingPlan.formatPrice(nextCyclePayInfo.finalPrice),
            selectedBillingPlan.getCycleDuration(true),
            nextBillingDate.format("MMM DD, YYYY")
        ];
        const updatePaymentInfo = applyPolicy === SubscriptionApplyPolicy.NEXT_BILLING_CYCLE
            ? [
                Messages.updateAtEndCyclePaymentInfo(...paymentInfoMessageArgs),
                Controller.subscriptionPlan.userHasEquivalentSubscriptionToSelected() ? "" : Messages.updateAtEndCycleChangeOffer,
            ].join("")
            : Messages.updateInstantlyPaymentInfo(selectedBillingPlan.formatPrice(instantPayInfo.finalPrice), ...paymentInfoMessageArgs);

        const checkboxElement = (instantPayInfo.finalPrice !== nextCyclePayInfo.finalPrice) && <CheckboxInput
            className={styleSheet.checkbox}
            testId="applyPolicyCheckbox"
            onChange={enabled => {
                Controller.subscriptionPlan.applyPolicy = enabled ? SubscriptionApplyPolicy.NEXT_BILLING_CYCLE :
                    SubscriptionApplyPolicy.INSTANTLY;
                this.redraw();
            }}
            initialValue={applyPolicy === SubscriptionApplyPolicy.NEXT_BILLING_CYCLE}
            label={Messages.updateAtEndCycleCheckbox(paidUntil.format("MMM DD, YYYY"))}
        />;

        return [
            <div className={styleSheet.lineSeparator}/>,
            <div className={styleSheet.reviewItem} testId="paymentInfoSection">
                {checkboxElement}
                <MessageElement message={updatePaymentInfo}/>
            </div>
        ];
    }

    render() {
        const {styleSheet} = this;
        const shippingAddress = Controller.address.selectedAddress;
        const preferredPaymentMethod = Controller.paymentMethod.selectedPaymentMethod;
        const {selectedOffer} = Controller.subscriptionPlan;
        const activeSubscription = iFrameUserDataService.getActiveSubscription();
        const selectedCoverage = selectedOffer == null ? activeSubscription.coverage : selectedOffer.coverage;
        const selectedBillingPlan = selectedOffer == null ? activeSubscription.billingPlan : selectedOffer.billingPlan;
        let discountInfo = Controller.subscriptionPlan.getDiscountInfo(selectedOffer);
        if (!discountInfo) {
            Controller.subscriptionPlan.updateEditPricesFromExistingSubscription();
            discountInfo = Controller.subscriptionPlan.getDiscountInfo(selectedOffer);
            if (!discountInfo) {
                return null;
            }
        }
        const finalPrice = selectedBillingPlan.formatPrice(discountInfo.finalPrice);
        const subscriptionPlanArgs = [selectedCoverage.name, finalPrice, selectedBillingPlan.getCycleDuration(true)];
        const subscriptionPlanText = Controller.subscriptionPlan.userHasEquivalentSubscriptionToSelected()
            ? Messages.editSubscriptionPlanDescription(...subscriptionPlanArgs)
            : (discountInfo.finalPrice === discountInfo.discountedBeforeTaxesPrice
                ? Messages.editSubscriptionChangePlanDescription(...subscriptionPlanArgs)
                : Messages.editSubscriptionChangePlanWithShippingIncludedDescription(...subscriptionPlanArgs));

        return [
            <div className={styleSheet.reviewItem} testId="subscriptionPlanSection">
                <div className={styleSheet.reviewItemTitleContainer}>
                    <div className={styleSheet.reviewItemTitle}>{Messages.subscriptionPlan}</div>
                    <LinkButton
                        href={SUBSCRIPTION_PLAN_PANEL_URL}
                        value={Messages.change}
                        testId="changeButton"
                    />
                </div>
                <div className={""}>
                    <MessageElement message={subscriptionPlanText}/>
                </div>
            </div>,
            (selectedCoverage.requiresAddress || (selectedOffer != null && selectedOffer.shouldCollectAddress())) && [
                <div className={styleSheet.lineSeparator}/>,
                <div className={styleSheet.reviewItem} testId="addressSection">
                    <div className={styleSheet.reviewItemTitleContainer}>
                        <div className={styleSheet.reviewItemTitle}>{Messages.address}</div>
                        <LinkButton
                            testId="editButton"
                            value={Messages.edit}
                            href={ADDRESS_PANEL_URL}
                        />
                    </div>
                    <div>
                        <div className={styleSheet.nameOnAddress}>{shippingAddress?.getRecipientName()}</div>
                        <div className={styleSheet.addressDetails}>{shippingAddress}</div>
                    </div>
                </div>
            ],
            <div className={styleSheet.lineSeparator}/>,
            <div className={styleSheet.reviewItem} testId="paymentMethodSection">
                <div className={styleSheet.reviewItemTitleContainer}>
                    <div className={styleSheet.reviewItemTitle}>{Messages.paymentMethod}</div>
                    <LinkButton
                        href={PAYMENT_DETAILS_PANEL_URL}
                        value={preferredPaymentMethod ? Messages.edit : Messages.addNewCard}
                        testId="editButton"
                    />
                </div>
                {
                    preferredPaymentMethod ?
                        <PaymentMethodDescription paymentMethod={preferredPaymentMethod}/>
                        : (
                        iFrameUserDataService.hasPrimaryPaymentMethod() && !iFrameMerchantService.isCDSPaymentMethod()?
                            <UsingBlinkDefaultPaymentMethod /> :
                            <div className={styleSheet.cardDetails}>
                                <div>{getPaymentMethodEmptySubscriptionMessage(activeSubscription)}</div>
                            </div>
                    )
                }
            </div>,
            this.renderCheckboxAndPriceInfo(),

            <MerchantButton
                disabled={this.isPrimaryButtonDisabled()}
                className={styleSheet.actionButton}
                onClick={() => Flow.subscribe.changeSubscription()}
                label={Messages.saveChanges}
            />,

            this.isPrimaryButtonDisabled() &&
                <div onClick={() => Router.changeURL(INLINE_EDIT_SUBSCRIPTION_URLS.cancel)}
                     className={styleSheet.backButton}>{Messages.cancelSubscription}
                </div>
        ];
    }

    onMount() {
        super.onMount();
        this.attachListener(Controller.address, () => this.redraw());
        this.attachListener(Controller.subscriptionPlan, () => {
            this.redraw();
        });
    }
}
